import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import {
  TableColumnAction,
  TableColumnTypes,
  TableConfig,
  TableStickyTypes,
} from '@next/core-lib/table';
import { UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';

import { CropAid } from 'src/app/crop-aid/crop-aid.model';
import * as CropAidSelectors from 'src/app/crop-aid/store/crop-aid.selectors';
import { ManureTabType } from 'src/app/manure-space/models/manure-space.models';
import { ManureSpaceApiService } from 'src/app/manure-space/services/manure-space-api.service';

import { setFertilizersFromDelivery } from 'src/app/manure-space/store/manure-space.actions';
import * as ManureSpaceSelectors from 'src/app/manure-space/store/manure-space.selectors';
import { SalesOrderPeriod } from 'src/app/sales-order-period/models/sales-order-period.models';
import * as SalesOrderPeriodSelectors from 'src/app/sales-order-period/store/sales-order-period.selectors';
import { SiteState } from 'src/app/shared/models';
import { SoilType } from 'src/app/soil-type/models/soil-type.models';
import { selectSoilTypes } from 'src/app/soil-type/store/soil-type.selectors';
import * as AppActionTypes from 'src/app/store/actions';
import * as AppSelectors from 'src/app/store/selectors';

import { FertilizerImportDialogComponent } from '../fertilizer-import-dialog/fertilizer-import-dialog.component';
import { InitialStockRvoCheckComponent } from '../initial-stock-rvo-check/initial-stock-rvo-check.component';

import { DeleteFertilizersByManureCode, Fertilizer } from '../models/fertilizer.model';
import * as FertilizersActions from '../store/actions';

const MAX_LENGTH_MANURECODE_WITH_PREFIX = 3;

interface FertilizerDisplayRow extends Fertilizer {
  periodName: string;
  soilTypeName: string;
  cropAidDisplayName: string;
  dosageDisplay: string;
}

export enum ButtonAction {
  EditFields = 'editFields',
  ImportVzc = 'importVzc',
  AnimalManureTransport = 'animalManureTransport',
  ImportMineralBalance = 'importMineralBalance',
  ImportAdvisedFertilizers = 'importAdvisedFertilizers',
}

@Component({
  selector: 'app-fertilizers-table',
  templateUrl: './fertilizers-table.component.html',
  styleUrls: ['./fertilizers-table.component.scss'],
  standalone: false,
})
export class FertilizersTableComponent implements OnInit, OnDestroy, OnChanges {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private readonly manureSpaceApi = inject(ManureSpaceApiService);

  tableConfig: TableConfig;

  private salesOrderPeriods: SalesOrderPeriod[];
  private cropAids: CropAid[];
  private soilTypes: SoilType[];

  @ViewChild('cropAidIdCell', { static: true }) cropAidIdsTemplateRef: TemplateRef<unknown>;
  @ViewChild('periodIdCell', { static: true }) periodIdsTemplateRef: TemplateRef<unknown>;
  @ViewChild('soilTypeIdCell', { static: true })
  soilTypeIdsTemplateRef: TemplateRef<unknown>;
  @ViewChild('dosageCell', { static: true }) dosagesTemplateRef: TemplateRef<unknown>;
  @ViewChild('manureCodeCell', { static: true }) manureCodeTemplateRef: TemplateRef<unknown>;

  @Input() fertilizers: Fertilizer[];
  @Input() title: string;
  @Input() showManureCode = false;
  @Input() manureTypeDetails = false;
  @Input() showInitialStockMatchesRvo = false;

  readonly initialStockRvoCheckComponent = InitialStockRvoCheckComponent;

  private viewWasInitialized = false;
  public activeManureTab: ManureTabType;

  private readonly destroyed$ = new Subject<boolean>();
  private readonly cropAids$ = this.store.select(CropAidSelectors.selectCropAidFertilizers);
  private readonly soilTypes$ = this.store.pipe(
    select(selectSoilTypes),
    takeUntil(this.destroyed$),
  );
  private readonly salesOrderPeriods$ = this.store.select(
    SalesOrderPeriodSelectors.selectSalesOrderPeriods,
  );
  public readonly cropYear$ = this.store.select(AppSelectors.getCropYear);
  public readonly availableCropYears$ = this.store.select(AppSelectors.getAvailableCropYears);
  public readonly defaultCropYear$ = this.store.select(AppSelectors.getDefaultCropYear);
  public readonly customer$ = this.store.select(UserSelectors.selectCustomer);
  private readonly manureSpace$ = this.store.pipe(
    select(ManureSpaceSelectors.selectActiveTabManureSpace),
  );

  ngOnChanges(): void {
    if (this.viewWasInitialized) {
      this.setTableConfig(this.fertilizers);
    }
  }

  ngOnInit() {
    combineLatest([this.salesOrderPeriods$, this.cropAids$, this.soilTypes$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([sop, cropAids, soilTypes]) => {
          this.salesOrderPeriods = sop;
          this.cropAids = cropAids;
          this.soilTypes = soilTypes;
          if (this.viewWasInitialized) {
            this.setTableConfig(this.fertilizers);
          }
        },
      });

    this.store
      .select(ManureSpaceSelectors.selectActiveTabIndex)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((activeManureTab: ManureTabType) => {
        this.activeManureTab = activeManureTab;
      });

    this.viewWasInitialized = true;
    this.setTableConfig(this.fertilizers);
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private deleteFertilizers(
    apiCall: (manureSpaceId: number) => Observable<unknown>,
    callback?: () => void,
  ) {
    if (this.tableConfig.dataSource.data.length === 1) {
      this.setTableConfig([]);
    }

    this.manureSpace$
      .pipe(
        take(1),
        switchMap((manureSpace) => {
          if (manureSpace.state === 'loaded') {
            return apiCall(manureSpace.data.id);
          }
        }),
      )
      .subscribe(() => {
        this.store.dispatch(FertilizersActions.fertilizersUpdated());
        if (callback) callback();
      });
  }

  deleteFertilizer(fertilizerId: string) {
    this.deleteFertilizers((manureSpaceId) =>
      this.manureSpaceApi.deleteFertilizer(manureSpaceId, fertilizerId),
    );
  }

  deleteFertilizersBy(fertilizer: Fertilizer) {
    const req: DeleteFertilizersByManureCode = {
      manureCode: fertilizer.manureCode,
      cropAidId: fertilizer.cropAidId,
      soilTypeId: fertilizer.soilTypeId,
      fertilizerType: fertilizer.fertilizerType,
    };

    this.deleteFertilizers((manureSpaceId) =>
      this.activeManureTab === ManureTabType.ManureType
        ? this.manureSpaceApi.deleteFertilizersByManureCode(manureSpaceId, req)
        : this.manureSpaceApi.deleteFertilizersByPeriod(manureSpaceId, fertilizer.periodId, req),
    );
  }

  upsertFertilizer(fertilizer: Fertilizer) {
    this.store.dispatch(
      setFertilizersFromDelivery({
        fertilizers: [fertilizer],
      }),
    );

    this.dialog
      .dialog(FertilizerImportDialogComponent, {
        title: this.locale.translate('edit'),
        confirmButton: true,
        fertilizerSource: fertilizer.fertilizerSource,
        isOverviewEditFertilizerId: fertilizer.id,
        isBatchEdit: false,
      })
      .pipe(
        switchMap(() => of(AppActionTypes.CloseImportDialog())),
        takeUntil(this.destroyed$),
      );
  }

  getPeriodName = (periodId: number): string =>
    this.salesOrderPeriods?.find((p) => p.id === periodId)?.name ?? '';

  getDosageDisplay = (dosage: number, cropAidId: number): string => {
    const uom = this.cropAids?.find((s) => s.id === cropAidId)?.unitOfMeasurement ?? '';
    return `${dosage.toFixed(1)} ${uom}`;
  };

  getSoilTypeName = (soilTypeId: number): string =>
    this.soilTypes?.find((s) => s.id === soilTypeId)?.name ?? '';

  getCropAidName(row: Fertilizer): string {
    const cropAidName = this.cropAids?.find((s) => s.id === row.cropAidId)?.name ?? '';
    if (!this.showManureCode || row.manureCode === null || row.manureCode === undefined) {
      return cropAidName;
    }
    const formattedManureCode = String(row.manureCode).padStart(
      MAX_LENGTH_MANURECODE_WITH_PREFIX,
      '0',
    );
    return `${formattedManureCode} ${cropAidName}`;
  }

  private toDisplayRows(fertilizers: Fertilizer[]): FertilizerDisplayRow[] {
    return (fertilizers ?? []).map((f) => ({
      ...f,
      periodName: this.getPeriodName(f.periodId),
      soilTypeName: this.getSoilTypeName(f.soilTypeId),
      cropAidDisplayName: this.getCropAidName(f),
      dosageDisplay: this.getDosageDisplay(f.dosage, f.cropAidId),
    }));
  }

  private getTableConfig(
    transports: FertilizerDisplayRow[],
    templateSelectors: {
      cropAidTemplateRef: TemplateRef<unknown>;
      periodTemplateRef: TemplateRef<unknown>;
      soilTypeTemplateRef: TemplateRef<unknown>;
      dosageTemplateRef: TemplateRef<unknown>;
    },
  ) {
    const columns = [
      {
        id: 'cropAidId',
        type: TableColumnTypes.TEMPLATE,
        label: this.manureTypeDetails
          ? this.locale.translate('fertilizers.tabs.ManureType')
          : this.locale.translate('sharedTransport.table.headers.fertilizer'),
        disableSort: true,
        className: 'flex__child--4',
        headerClassName: 'flex__child--4',
        template: templateSelectors.cropAidTemplateRef,
      },
      {
        id: 'comment',
        type: TableColumnTypes.STRING,
        label: this.locale.translate('comments'),
        disableSort: true,
        className: 'flex__child--4',
        headerClassName: 'flex__child--4',
      },
      {
        id: 'periodId',
        type: TableColumnTypes.TEMPLATE,
        label: this.locale.translate('period'),
        disableSort: true,
        className: 'flex__child--2',
        headerClassName: 'flex__child--2',
        template: templateSelectors.periodTemplateRef,
      },
      {
        id: 'soilTypeId',
        type: TableColumnTypes.TEMPLATE,
        label: this.locale.translate('soilType'),
        disableSort: true,
        className: 'flex__child--3',
        headerClassName: 'flex__child--3',
        template: templateSelectors.soilTypeTemplateRef,
      },
      {
        id: 'dosage',
        type: TableColumnTypes.TEMPLATE,
        label: this.locale.translate('dosage'),
        disableSort: true,
        className: 'flex__child--2',
        headerClassName: 'flex__child--2',
        template: templateSelectors.dosageTemplateRef,
      },
      {
        id: 'totalNitrogen',
        type: TableColumnTypes.NUMBER,
        label: this.locale.translate('sharedTransport.table.headers.nitrogenInKg'),
        disableSort: true,
        className: 'flex__child--2',
        headerClassName: 'flex__child--2',
        digitsInfo: '1.0-0',
      },
      {
        id: 'totalPhosphate',
        type: TableColumnTypes.NUMBER,
        label: this.locale.translate('sharedTransport.table.headers.phosphateInKg'),
        disableSort: true,
        className: 'flex__child--2',
        headerClassName: 'flex__child--2',
        digitsInfo: '1.0-0',
      },
    ];

    let actionRows: TableColumnAction[] =
      this.activeManureTab === ManureTabType.ManureDetails
        ? [
            {
              id: 'edit',
              label: this.locale.translate('edit'),
              callback: (row: Fertilizer) => {
                this.upsertFertilizer(row);
              },
              icon: 'edit',
              disabled: () => false,
            },
            {
              id: 'delete',
              label: this.locale.translate('delete'),
              callback: (row: Fertilizer) => {
                this.deleteFertilizer(row.id);
              },
              icon: 'delete',
              disabled: () => false,
            },
          ]
        : [
            {
              id: 'delete',
              label: this.locale.translate('delete'),
              callback: (row: Fertilizer) => {
                this.deleteFertilizersBy(row);
              },
              icon: 'delete',
              disabled: () => false,
            },
          ];

    return {
      dataSource: new MatTableDataSource<FertilizerDisplayRow>(transports),
      columns,
      actionColumn: true,
      actions: actionRows,
      hideActionLabel: true,
      selectColumn: false,
      pagination: false,
      header: TableStickyTypes.STICKY,
      primaryColumn: ' ',
      alternateRows: true,
    } as TableConfig;
  }

  private setTableConfig(fertilizers: Fertilizer[]) {
    this.tableConfig = this.getTableConfig(this.toDisplayRows(fertilizers), {
      cropAidTemplateRef: this.cropAidIdsTemplateRef,
      periodTemplateRef: this.periodIdsTemplateRef,
      soilTypeTemplateRef: this.soilTypeIdsTemplateRef,
      dosageTemplateRef: this.dosagesTemplateRef,
    });
  }
}
